import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import ProgressRing from "@/components/ProgressRing";
import { CheckCircle2, Clock, BookOpen, Lock } from "lucide-react";

export default function ModuleCard({ module, prog, index, locked }) {
  const percent = prog?.percent ?? 0;
  const complete = prog?.complete;
  const lessonsDone = prog?.lessons_done ?? 0;
  const [imgError, setImgError] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/module/${module.id}`}
        data-testid={`module-card-${module.id}`}
        className="group relative block rounded-2xl overflow-hidden glass hover:glow-gold transition-all duration-500 h-full"
      >
        <div className="relative h-40 overflow-hidden">
          {module.cover && !imgError ? (
            <img
              src={module.cover}
              alt={module.title}
              loading="lazy"
              decoding="async"
              onError={() => setImgError(true)}
              className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#1b212d] to-[#0c0f15] flex items-center justify-center">
              <span className="font-heading text-6xl font-bold text-white/10">
                {String(module.number).padStart(2, "0")}
              </span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
          <div className="absolute top-3 left-3 flex items-center gap-2">
            <span className="font-mono text-xs px-2 py-1 rounded-md bg-black/50 backdrop-blur border border-white/10 text-gold">
              {String(module.number).padStart(2, "0")}
            </span>
            {complete && (
              <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-gold/20 border border-gold/40 text-gold">
                <CheckCircle2 className="h-3 w-3" /> Abgeschlossen
              </span>
            )}
          </div>
          <div className="absolute top-3 right-3">
            {locked ? (
              <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-black/60 backdrop-blur border border-gold/30 text-gold" data-testid={`module-locked-${module.id}`}>
                <Lock className="h-3 w-3" /> Vollzugang
              </span>
            ) : (
              <ProgressRing percent={percent} size={48} stroke={4} showLabel={percent > 0} />
            )}
          </div>
          {locked && <div className="absolute inset-0 bg-[#0a0d12]/40" />}
        </div>

        <div className="p-5">
          <h3 className="font-heading font-bold text-lg leading-tight group-hover:text-gold transition-colors">
            {module.title}
          </h3>
          <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">{module.subtitle}</p>

          <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <BookOpen className="h-3.5 w-3.5" />
              {lessonsDone}/{module.lesson_count} Lektionen
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {module.duration_min} Min
            </span>
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-transparent via-gold to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </Link>
    </motion.div>
  );
}
